import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { Crown } from 'lucide-react'
import { Button } from '@/components/base/buttons'
import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter } from '@/components/ui/Modal'
import { toast } from '@/lib/toast'
import { useRequestPremiumVoice, voiceKeys } from '@/hooks/api'
import type { Voice } from '@/types/assistant'

interface PremiumRequestModalProps {
  /** The premium voice being requested; null keeps the modal closed. */
  voice: Voice | null
  assistantId: number
  onClose: () => void
  onRequested: (voice: Voice) => void
}

/**
 * Confirmation dialog that asks the CSM to add the Premium Voice product for
 * the assistant. Shared by the picker rows and the selected-voice card.
 */
export function PremiumRequestModal({
  voice,
  assistantId,
  onClose,
  onRequested,
}: PremiumRequestModalProps) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const requestPremiumVoice = useRequestPremiumVoice()

  const handleConfirm = () => {
    if (!voice) return
    requestPremiumVoice.mutate(
      { assistantId, voiceId: voice.id },
      {
        onSuccess: () => {
          onRequested(voice)
          onClose()
          toast.success(t('resources.assistants.voice_drawer.premium_request.success'))
          // A CSM-side upgrade changes available_in_plan; refetch instead of
          // waiting out the picker's staleTime.
          void queryClient.invalidateQueries({ queryKey: voiceKeys.lists() })
        },
        onError: () => {
          toast.error(t('resources.assistants.voice_drawer.premium_request.error'))
        },
      }
    )
  }

  return (
    <Modal
      size="md"
      isOpen={voice !== null}
      onOpenChange={(open) => !open && onClose()}
      isDismissable
    >
      <ModalContent>
        <ModalHeader icon={<Crown />} iconVariant="brand" onClose={onClose} bordered={false}>
          {t('resources.assistants.voice_drawer.premium_request.title')}
        </ModalHeader>
        <ModalBody className="text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('resources.assistants.voice_drawer.premium_request.description', {
              name: voice?.display_name ?? '',
            })}
          </p>
          <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
            {t('resources.assistants.voice_drawer.premium_request.info')}
          </p>
        </ModalBody>
        <ModalFooter bordered={false} align="stretch">
          <Button type="button" color="secondary" onPress={onClose}>
            {t('resources.assistants.voice_drawer.premium_request.cancel')}
          </Button>
          <Button
            type="button"
            color="primary"
            onPress={handleConfirm}
            isDisabled={requestPremiumVoice.isPending}
          >
            {t('resources.assistants.voice_drawer.premium_request.submit')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default PremiumRequestModal
